import {
  FormControl,
  InputAdornment,
  TextField,
  Typography
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import NumberFormat from "react-number-format";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { compose } from "underscore";
import {
  AlertInfo,
  CustomButton,
  DefaultLoader,
  InfoModal,
  NumberFormatCustom,
  StepCaption,
  StepNumber,
} from "../../components";
import { images } from "../../constants";
import { setStep } from "../../globalRedux/actions";
import { dataLayerGTM } from "../../utils/functions/dataLayerGTM";
import { setLastAmountSave } from "../deposit/redux/action";
import { resetSelectedPromo } from "../promoList/redux/action";
import { getGoalInterest } from "../redux/action";
import { CATEGORY_SAVING, GOAL_CREATION } from "../redux/constant";
import { createGoal, resetErrorGoalCreation } from "./redux/action";
import {
  Btn,
  Container,
  Footer,
  Img,
  ImgContainer,
  ImgLabel,
  Info,
  Month,
} from "./styles";

const label = {
  fontSize: "14px",
  fontWeight: "normal",
  lineHeight: 1.43,
  color: "#616367",
};

const input = {
  color: "#333",
  fontSize: "16px",
  fontWeight: 700,
  lineHeight: 1.5,
};

const StepThree = props => {
  const {
    selectedGoal,
    loading,
    savingGoalDetails,
    savingType,
    setStep,
    createGoal,
    goalIsExist,
    getGoalInterest,
    goalInterest,
    history,
    location,
    goalCreationLoading,
    goalCreationRSPLoading,
    createGoalLoading,
    errorCreateGoal,
    resetErrorGoalCreation,
    tempCifPlanId,
    resetSelectedPromo,
    setLastAmountSave,
  } = props;

  const [goalDetails, setGoalDetails] = useState(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isProjectedReturnInfoOpen, setIsProjectedReturnInfoOpen] = useState(false);
  const [isExist, setIsExist] = useState(false);
  const alreadySubmit = useRef(null);

  /** Save current step into redux */
  useEffect(() => {
    setLastAmountSave();
    resetSelectedPromo();
    getGoalInterest(selectedGoal.refId);
    setStep("/step-three");
    sessionStorage.removeItem("stopRsp");
  }, []);

  /** Save category saving data into local state */
  useEffect(() => {
    if (savingType === CATEGORY_SAVING && savingGoalDetails) {
      setGoalDetails(savingGoalDetails);
    }
  }, [savingGoalDetails]);

  useEffect(() => {
    if (goalIsExist.isExist) {
      setIsExist(true);
      alert("Goal name is already exists");
    } else {
      setIsExist(false);
    }
  }, [goalIsExist]);

  const handleCreateGoal = () => {
    if (alreadySubmit.current) return;
    alreadySubmit.current = true;

    const payload = {
      productId: goalDetails.productId,
      invsPlanId: goalDetails.invsPlanId,
      term: goalDetails.frequencyMonth,
      targetAmount:
        goalDetails.targetAmount &&
        parseFloat(
          goalDetails.targetAmount.replace(/[^0-9\.]/gi, "")
        ).toString(),
      planAlias: goalDetails.planAlias,
    };

    if (tempCifPlanId) {
      history.push(`/make-deposit/${GOAL_CREATION}`);
    } else {
      createGoal(payload, () => history.push(`/make-deposit/${GOAL_CREATION}`));
    }
  };

  return (
    <Container>
      {createGoalLoading && <DefaultLoader />}
      <AlertInfo
        description="We are facing some technical issues, and we are working very hard to resolve the issue."
        isOpen={errorCreateGoal}
        isTimeout
        handleAction={() => {
          resetErrorGoalCreation();
          alreadySubmit.current = false;
        }}
      />
      <InfoModal
        open={isInfoOpen}
        handleClose={() => setIsInfoOpen(false)}
        title="Projected Net Return"
        message="Projected Net Return is the estimated return on your investment over the next 12 months."
      />
      <InfoModal
        open={isProjectedReturnInfoOpen}
        handleClose={() => setIsProjectedReturnInfoOpen(false)}
        title="Projected Return Rate"
        message="Projected returns are based on current performance data only and are provided for illustration. Past and present performance is not indicative of future results."
      />
      <StepNumber
        onClick={() => history.push("/step-two")}
        text="Step 3 of 3"
      />
      <StepCaption text="Your goal summary" />
      <ImgContainer>
        <Img
          src={
            goalDetails && goalDetails.image
              ? goalDetails.image
              : images.generalSavings
          }
          alt={goalDetails && goalDetails.planAlias}
        />
        <ImgLabel>{goalDetails && goalDetails.planAlias}</ImgLabel>
      </ImgContainer>
      <Info>
        {goalDetails && goalDetails.targetAmount && (
          <FormControl>
            <Typography sx={label}>Savings Target</Typography>
            <TextField
              value={
                goalDetails.targetAmount
                  ? parseFloat(
                      goalDetails.targetAmount.replace(/[^0-9\.]/gi, "")
                    )
                  : 0
              }
              variant="standard"
              InputProps={{
                inputComponent: NumberFormatCustom,
                readOnly: true,
                sx: input,
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography
                      color="textPrimary"
                      sx={{
                        fontFamily: "FSElliotPro",
                        fontSize: "16px",
                        fontWeight: 700,
                        lineHeight: 1.5,
                      }}
                    >
                      RM
                    </Typography>
                  </InputAdornment>
                ),
              }}
              inputProps={{
                "data-cy": "goal-saving-target",
              }}
            />
          </FormControl>
        )}
        <FormControl>
          <Typography sx={label}>Savings Period</Typography>
          <TextField
            value={
              goalDetails && goalDetails.frequencyMonth
                ? `${goalDetails.frequencyMonth} Months`
                : "0 Months"
            }
            variant="standard"
            InputProps={{
              readOnly: true,
              sx: {
                color: "#333",
                fontWeight: 700,
                lineHeight: 1.5,
              },
              "data-cy": "goal-saving-period",
            }}
          />
        </FormControl>
        <FormControl>
          <Typography sx={label}>Projected Net Return</Typography>
          <TextField
            value={(goalDetails && goalDetails.projectedReturn) || 0}
            variant="standard"
            InputProps={{
              inputComponent: NumberFormatCustom,
              readOnly: true,
              sx: input,
              startAdornment: (
                <InputAdornment position="start">
                  <Typography
                    color="textPrimary"
                    sx={{ fontWeight: 700, lineHeight: 1.5 }}
                  >
                    RM
                  </Typography>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <InfoOutlinedIcon
                    color="primary"
                    sx={{ fontSize: 20 }}
                    onClick={() => {
                      dataLayerGTM(
                        "click_tag",
                        "Click",
                        "Info",
                        "Projected Net Return"
                      );
                      setIsInfoOpen(true);
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
        <FormControl>
          <Typography sx={label}>Projected Return Rate</Typography>
          <TextField
            value={
              goalInterest && goalInterest.expPerf
                ? `${goalInterest.expPerf.toFixed(2)}%`
                : 0
            }
            variant="standard"
            InputProps={{
              readOnly: true,
              sx: input,
              endAdornment: (
                <InputAdornment position="end">
                  <InfoOutlinedIcon
                    className="material-icons-outlined"
                    color="primary"
                    sx={{ fontSize: 20 }}
                    onClick={() => {
                      dataLayerGTM(
                        "click_tag",
                        "Click",
                        "Info",
                        "Projected Return Rate"
                      );
                      setIsProjectedReturnInfoOpen(true);
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
      </Info>
      <Footer>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "normal",
            lineHeight: 1.43,
            color: "#616367",
          }}
        >
          Monthly Cash In
        </Typography>
        <Typography
          color="textPrimary"
          sx={{
            fontSize: "32px",
            fontWeight: 700,
            lineHeight: "normal",
            marginTop: "4px",
          }}
        >
          RM{" "}
          <NumberFormat
            value={goalDetails ? goalDetails.regularInvsAmt : ""}
            thousandSeparator
            displayType="text"
            decimalScale={2}
            fixedDecimalScale={true}
          />{" "}
          <Month>/ month</Month>
        </Typography>
      </Footer>
      <Btn>
        <CustomButton
          text="Make First Cash In"
          variant="contained"
          color="#fff"
          backgroundColor="#1278cc"
          onClick={() => {
            dataLayerGTM(
              "click_tag",
              "Click",
              "Button",
              "Make First Deposit Button"
            );
            handleCreateGoal();
          }}
          disabled={
            isExist ||
            goalCreationLoading ||
            goalCreationRSPLoading ||
            createGoalLoading
          }
        />
      </Btn>
    </Container>
  );
};

const withConnect = connect(
  state => ({
    loading: state.goal.loading,
    savingGoalDetails: state.goal.savingGoalDetails,
    savingType: state.goal.savingType,
    goalIsExist: state.goal.goalIsExist,
    selectedGoal: state.goal.selectedGoal,
    goalInterest: state.goal.goalInterest,
    goalCreationLoading: state.goal.goalCreationLoading,
    goalCreationRSPLoading: state.goal.goalCreationRSPLoading,
    ...state.stepThreeReducer,
  }),
  {
    setStep,
    createGoal,
    getGoalInterest,
    resetErrorGoalCreation,
    resetSelectedPromo,
    setLastAmountSave,
  }
);

export default compose(
  withRouter,
  withConnect
)(StepThree);
